import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Turn as Hamburger } from 'hamburger-react';
import ProfileController from '@/services/ProfileController';
import useWindowSize from '@/utils/useWindowSize';
import Login from '@/components/auth/Login';
import Dropdown from '@/ui/dropdown/Dropdown';
import useStore from '@/store/store';
import OutsideClickHandler from '@/shared/OutsideClickHandler';
import Button from '@/ui/button/Button';
import { useCommStore } from '@/components/communication/commStore';
import { useInterestPolling, useInterestStore } from '@/components/dashboard/interestStore';
import s from './s.module.css';

const categoriesShop = [
	{ label: 'Live', value: '/live' },
	{ label: 'Παραγγελίες', value: '/orders' },
];

const catalogCategory = { label: 'Κατάλογος', value: '/catalog' };

const categoriesAdmin = [
	{ label: 'Καταστήματα', value: '/shops' },
	{ label: 'Διανομείς', value: '/delivery' },
	{ label: 'Ρυθμίσεις', value: '/settings' },
	{ label: 'Στατιστικά', value: '/statistics' },
];

const categoriesMaster = [
	{ label: 'Διαχειριστές', value: '/admins' },
	{ label: 'Πόλεις', value: '/cities' },
	{ label: 'Επισκόπηση', value: '/dashboard' },
];

const HeaderPanel = () => {
	const [active, setActive] = useState(false);

	const navigate = useNavigate();
	const location = useLocation();

	const { user, agreeToTerms, setActiveOrders } = useStore();
	// Μηνύματα: shown when the Communication Center is usable for this user.
	const commMe = useCommStore(s => s.me);
	const togglePanel = useCommStore(s => s.togglePanel);
	// Μηνύματα opens / closes the messages column on the left; admins reach
	// the full page from the column (or are already on it). While the chat is
	// off, an admin who may switch it on (or read the audit log) goes to the
	// page, where those settings are.
	const messagesItem = {
		label: 'Μηνύματα',
		value: '/messages',
		badge: commMe?.unread || 0,
		onClick: () => {
			if (location.pathname === '/messages') return;
			if (commMe?.canUse) togglePanel();
			else navigate('/messages');
		},
	};
	const adminSeesMessages = commMe?.canUse || commMe?.canToggleChat || commMe?.canViewAudit;
	// Masters: new requests from the website's interest form, on «Επισκόπηση».
	useInterestPolling(user);
	const newInterest = useInterestStore(s => s.newCount);
	// Shops have no Μηνύματα page.
	const shopMessagesItem = { ...messagesItem, value: '#messages', onClick: togglePanel };

	const size = useWindowSize({ full: true });
	const { width } = size;

	const dropdownRef = useRef();
	const loginDialogRef = useRef();

	useEffect(() => {
		document.title =
			typeof window?.location?.href === 'string' && window.location.href.includes('solvd')
				? 'Solvd'
				: 'Delivery Man';
	}, []);

	const currentRoute = useMemo(() => {
		return location.pathname;
	}, [location]);

	const categories = useMemo(() => {
		if (!user?.role) {
			return [];
		}

		const catalog = user?.cityLocation?.catalog;
		const correctCategoriesShop =
			catalog && typeof catalog === 'object' && Object.keys(catalog)?.length
				? [...categoriesShop, catalogCategory]
				: [...categoriesShop];

		const { role } = user;

		if (role === 'shop') {
			// Shops never get the Διανομείς page, whatever the city settings say.
			// Μηνύματα right after Live, when the city's chat is on for them.
			return commMe?.canUse
				? [correctCategoriesShop[0], shopMessagesItem, ...correctCategoriesShop.slice(1)]
				: [...correctCategoriesShop];
		} else if (role === 'admin') {
			return [
				...correctCategoriesShop,
				...categoriesAdmin,
				...(adminSeesMessages ? [messagesItem] : []),
				...(user.managerLevel === 1 ? [categoriesMaster.find(it => it?.value === '/admins')] : []),
			];
		} else if (role === 'master') {
			return [
				...correctCategoriesShop,
				...categoriesAdmin,
				messagesItem,
				...categoriesMaster.map(it =>
					it.value === '/dashboard' ? { ...it, badge: newInterest } : it,
				),
			];
		}

		return [];
	}, [user, commMe?.canUse, commMe?.canToggleChat, commMe?.canViewAudit, commMe?.unread, newInterest]); // eslint-disable-line react-hooks/exhaustive-deps

	const cityItems = useMemo(() => {
		if (!Array.isArray(user?.cities)) {
			return [];
		}

		return user.cities
			.map(city => ({
				value: city.id,
				label: city.name,
			}))
			.sort((a, b) => a.label.localeCompare(b.label, 'el'));
	}, [user]);

	const openCityModal = () => {
		dropdownRef.current.parentToggleModal(true, true);
	};

	const onCitySelect = async value => {
		if (user?.cityLocation?.id === value) {
			return;
		}

		dropdownRef.current.parentToggleModal(false);

		localStorage.setItem('last-city-used', value);

		window.location.reload();
		setActiveOrders(null);
	};

	const navigateToHome = () => {
		setActive(false);
		navigate('/');
	};

	const onAcceptTerms = () => {
		agreeToTerms(true);
		ProfileController.agreeToTerms();
	};

	const openLoginDialog = () => {
		loginDialogRef.current?.showModal();
	};

	const closeLoginDialog = () => {
		loginDialogRef.current?.close();
	};

	const onLoginDialogBackdropClick = e => {
		if (e.target === loginDialogRef.current) {
			closeLoginDialog();
		}
	};

	const isHome = location.pathname === '/';

	const logoUrl =
		typeof window?.location?.href === 'string' && window.location.href.includes('solvd')
			? 'https://res.cloudinary.com/dy6bbey4u/image/upload/v1738661573/h7lwvafvdvqmyfnodd64.jpg'
			: user?.cityLocation?.logoUrl;

	// Logged-out visitors on the public website get its own header (with its
	// own «Σύνδεση»); the white-label brand keeps this one.
	const isPublicPage = ['/', '/perioxes', '/paraggelia', '/politiki-aporritou', '/3d'].includes(
		location.pathname,
	);
	if (!user && isPublicPage && !window.location.href.includes('solvd')) {
		return null;
	}

	return (
		<>
			{user?.role === 'shop' && !user?.termsAgreed && !currentRoute?.startsWith('/terms') && (
				<div className={s.terms_container}>
					<div className={s.terms_label}>
						Χρησιμοποιώντας την εφαρμογή συμφωνείτε με τους{' '}
						<span className={s.terms_link}>
							<Link to='/terms' target='_blank'>
								όρους χρήσης.
							</Link>
						</span>
					</div>
					<Button
						text='Το κατάλαβα'
						onClick={onAcceptTerms}
						extraStyle={s.terms_button}
						extraTextStyle={s.terms_button_text}
					/>
				</div>
			)}
			{width >= 861 ? (
				<>
					<div className={`${s.headerContainerDesktop} ${isHome ? s.headerContainerHome : ''}`}>
						<div className='flex flex-col gap-y-4'>
							<Link to='/'>
								<div className={s.logo} style={{ marginTop: 16 }} onClick={navigateToHome}>
									<img
										src={logoUrl || '/images/logo_rect.png'}
										width={!logoUrl ? 1419 / 8 : 100}
										height={!logoUrl ? 319 / 8 : 100}
										style={{ objectFit: 'contain' }}
										alt='Logo'
									/>
								</div>
							</Link>
							{user?.role === 'master' && (
								<div className={s.cityContainer}>
									<div
										className='text-neutral-850! cursor-pointer rounded-sm border-1 bg-[#47d3c9] px-3 py-1 select-none'
										onClick={openCityModal}
									>
										{user?.cityLocation?.name}
									</div>
									<OutsideClickHandler
										onOutsideClick={() => dropdownRef.current.parentToggleModal(false)}
									>
										<Dropdown
											ref={dropdownRef}
											onChange={onCitySelect}
											options={cityItems}
											value={user?.cityLocation?.id}
											height={280}
										/>
									</OutsideClickHandler>
								</div>
							)}
						</div>

						<div className='flex flex-1 flex-row flex-wrap gap-y-2'>
							{categories &&
								categories.map(c => (
									<Link
										to={c.value}
										key={c.value}
										onClick={e => {
											if (c.onClick) {
												e.preventDefault();
												c.onClick();
											}
										}}
										className={s.pointer}
										style={{ cursor: 'pointer' }}
									>
										<div
											className={`${!isHome ? s.item : s.itemHome} ${
												!isHome
													? currentRoute === c.value
														? s.itemSelected
														: ''
													: currentRoute === c.value
														? s.itemSelectedHome
														: ''
											} : ''}`}
										>
											{c.label}
											{!!c.badge && (
												<span className='ml-1 rounded-full bg-red-500 px-1.5 text-[11px] font-semibold text-white'>
													{c.badge}
												</span>
											)}
										</div>
									</Link>
								))}
						</div>

						<div className='flex flex-col'>
							<button type='button' className={s.loginTriggerButton} onClick={openLoginDialog}>
								{user?.token ? user.displayName : 'Σύνδεση'}
							</button>

							<dialog
								ref={loginDialogRef}
								className={s.loginDialog}
								onClick={onLoginDialogBackdropClick}
							>
								<div className={s.loginDialogContent}>
									<button
										type='button'
										className={s.loginDialogClose}
										onClick={closeLoginDialog}
										aria-label='Κλείσιμο'
									>
										×
									</button>
									<Login
										onLoginCb={() => {
											setActive(false);
											closeLoginDialog();
										}}
										onLogoutCb={() => {
											closeLoginDialog();
										}}
									/>
								</div>
							</dialog>
						</div>
					</div>
				</>
			) : (
				<>
					<div className={`${s.header_container_mobile} ${isHome ? s.headerContainerHome : ''}`}>
						<button
							onClick={() => {
								setActive(!active);
							}}
							className={s.burger_container}
							type='button'
						>
							<Hamburger direction='right' toggled={active} color='rgb(50,50,50)' />
						</button>
						<div
							style={{
								display: 'flex',
								flexDirection: 'row',
								alignItems: 'center',
								marginLeft: 20,
								flex: 1,
							}}
						>
							<div className={`${s.logo} ${s.logoMobile}`} onClick={navigateToHome}>
								<img
									src={logoUrl || '/images/logo_rect.png'}
									width={!logoUrl ? 1419 / 8 : 80}
									height={!logoUrl ? 319 / 8 : 80}
									style={{ objectFit: 'contain' }}
									alt='Logo'
								/>
							</div>
							{user?.role === 'master' && (
								<div className={s.cityContainer}>
									<div
										className='text-neutral-850! ml-5 cursor-pointer rounded-sm border-1 bg-[#47d3c9] px-3 py-1 select-none'
										onClick={openCityModal}
									>
										{user?.cityLocation?.name || 'Πόλη'}
									</div>
									<OutsideClickHandler
										onOutsideClick={() => dropdownRef.current.parentToggleModal(false)}
									>
										<Dropdown
											ref={dropdownRef}
											onChange={onCitySelect}
											options={cityItems}
											height={260}
										/>
									</OutsideClickHandler>
								</div>
							)}
						</div>
					</div>

					{active && (
						<div className={s.mobile_main_container}>
							<div
								style={{
									display: 'flex',
									flexDirection: 'row',
									justifyContent: 'flex-end',
									paddingLeft: 20,
									paddingRight: 16,
									alignItems: 'center',
									fontSize: 18,
								}}
							>
								<Login onLoginCb={() => setActive(false)} />
							</div>
							<div className={s.mobile_categories_container}>
								{categories &&
									categories.map(c => (
										<Link
											to={c.value}
											key={c.value}
											onClick={e => {
												if (c.onClick) {
													e.preventDefault();
													c.onClick();
												}
											}}
										>
											<div onClick={() => setActive(false)} className={s.itemMobile}>
												{c.label}
												{!!c.badge && (
													<span className='ml-1 rounded-full bg-red-500 px-1.5 text-[11px] font-semibold text-white'>
														{c.badge}
													</span>
												)}
											</div>
										</Link>
									))}
							</div>
						</div>
					)}
				</>
			)}
		</>
	);
};

export default HeaderPanel;
